import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getMapMarkers, isMappable, listingFocusPoint } from "@/lib/geo";
import { useLang } from "@/lib/i18n";

const clusterPx = (count) => (count >= 20 ? 52 : count >= 5 ? 44 : 36);

const esc = (s) => String(s)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/"/g, "&quot;");

const clusterIcon = (count, highlighted) => {
  const s = clusterPx(count);
  return L.divIcon({
    className: "rt-map-icon",
    html: `<div class="rt-cluster${highlighted ? " is-highlighted" : ""}" style="width:${s}px;height:${s}px">${count}</div>`,
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
};

const priceIcon = (label, highlighted) => L.divIcon({
  className: "rt-map-icon",
  html: `<div class="rt-price-pin${highlighted ? " is-highlighted" : ""}">${esc(label)}</div>`,
  iconSize: [1, 1],
  iconAnchor: [0, 0],
});

const toBounds = (b) => ({
  west: b.getWest(),
  south: b.getSouth(),
  east: b.getEast(),
  north: b.getNorth(),
});

const MapController = ({ onViewport, fitKey, mappable, invalidateKey, skipFit }) => {
  const map = useMap();
  const lastFit = useRef("");

  useMapEvents({
    moveend: () => onViewport({ bounds: toBounds(map.getBounds()), zoom: map.getZoom() }),
    zoomend: () => onViewport({ bounds: toBounds(map.getBounds()), zoom: map.getZoom() }),
  });

  useEffect(() => {
    if (skipFit) return;
    if (!mappable.length) return;
    if (fitKey === lastFit.current) return;
    lastFit.current = fitKey;
    const b = L.latLngBounds(mappable.map((it) => [it.location.lat, it.location.lng]));
    if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 12 });
  }, [fitKey, mappable, map, skipFit]);

  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 60);
    return () => window.clearTimeout(id);
  }, [invalidateKey, map]);

  return null;
};

const FlyToOnCluster = ({ feature, listingsById, nonce }) => {
  const map = useMap();
  const last = useRef(0);
  useEffect(() => {
    if (!feature || nonce === last.current) return;
    last.current = nonce;
    const pts = feature.listingIds
      .map((id) => listingsById.get(id))
      .filter(isMappable)
      .map((it) => [it.location.lat, it.location.lng]);
    if (!pts.length) return;
    if (pts.length === 1) map.setView(pts[0], Math.max(map.getZoom(), 16), { animate: true });
    else map.fitBounds(pts, { padding: [48, 48], maxZoom: 17, animate: true });
  }, [feature, nonce, listingsById, map]);
  return null;
};

const FlyToFocus = ({ listingId, listingsById, nonce }) => {
  const map = useMap();
  const last = useRef("");
  useEffect(() => {
    if (!listingId) return;
    const key = `${listingId}:${nonce}`;
    if (key === last.current) return;
    const listing = listingsById.get(listingId);
    const point = listingFocusPoint(listing);
    if (!point) return;
    last.current = key;
    const id = window.setTimeout(() => {
      map.setView([point.lat, point.lng], point.zoom, { animate: true });
    }, 80);
    return () => window.clearTimeout(id);
  }, [listingId, nonce, listingsById, map]);
  return null;
};

export const ResultsMap = ({
  listings,
  fitKey,
  invalidateKey,
  highlightedListingId,
  focusListingId,
  focusNonce,
  onViewport,
  onHoverIds,
  onLockIds,
}) => {
  const { t } = useLang();
  const [zoom, setZoom] = useState(5);
  const [bounds, setBounds] = useState(null);
  const [clusterFly, setClusterFly] = useState({ feature: null, nonce: 0 });

  const mappable = useMemo(() => listings.filter(isMappable), [listings]);
  const listingsById = useMemo(() => new Map(mappable.map((it) => [it.id, it])), [mappable]);
  const focusPoint = listingFocusPoint(listingsById.get(focusListingId));

  const markers = useMemo(
    () => getMapMarkers(mappable, zoom, bounds, t),
    [mappable, zoom, bounds, t]
  );

  const reportViewport = ({ bounds: b, zoom: z }) => {
    setBounds(b);
    setZoom(z);
    onViewport?.({ bounds: b, zoom: z });
  };

  if (!mappable.length) {
    return (
      <div data-testid="map-empty" className="h-full w-full flex items-center justify-center bg-slate-100 text-slate-500 text-sm font-semibold px-6 text-center">
        {t("location_unavailable")}
      </div>
    );
  }

  return (
    <div data-testid="results-map" className="h-full w-full relative">
      <MapContainer
        center={focusPoint ? [focusPoint.lat, focusPoint.lng] : [55.75, 37.62]}
        zoom={focusPoint ? focusPoint.zoom : 5}
        className="h-full w-full"
        zoomControl
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <MapController
          onViewport={reportViewport}
          fitKey={fitKey}
          mappable={mappable}
          invalidateKey={invalidateKey}
          skipFit={!!focusPoint}
        />
        <FlyToFocus
          listingId={focusListingId}
          nonce={focusNonce}
          listingsById={listingsById}
        />
        <FlyToOnCluster
          feature={clusterFly.feature}
          nonce={clusterFly.nonce}
          listingsById={listingsById}
        />
        {markers.map((m) => {
          const highlighted = !!(highlightedListingId && m.listingIds.includes(highlightedListingId));
          const icon = m.type === "building"
            ? priceIcon(m.label, highlighted)
            : clusterIcon(m.count, highlighted);
          return (
            <Marker
              key={m.key}
              position={[m.lat, m.lng]}
              icon={icon}
              zIndexOffset={highlighted ? 1000 : m.type === "building" ? 200 : 100}
              eventHandlers={{
                mouseover: () => onHoverIds?.(m.listingIds),
                mouseout: () => onHoverIds?.(null),
                click: () => {
                  if (m.type === "building") onLockIds?.(m.listingIds);
                  else setClusterFly((s) => ({ feature: m, nonce: s.nonce + 1 }));
                },
              }}
            />
          );
        })}
      </MapContainer>
    </div>
  );
};
